import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { activateDataHygiene } from 'api/contacts/data-hygiene';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useDataHygieneMetrics } from 'customHooks/useDataHygieneMetrics';
import DataHygieneFeature from 'images/data-hygiene-feature.webp';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

const ActivationInterstitial = () => {
    const { accountId, isCurrentlyOnFreeTrial } = useCurrentAccount();
    const { data: metrics } = useDataHygieneMetrics();
    const { success: toastSuccess, error: toastError } = useToast();
    const contactsTotal = metrics.individuals.total;

    const { mutate: activate, isPending: activateIsPending } = useMutation({
        mutationFn: () => activateDataHygiene(accountId),
        onSuccess: data => {
            toastSuccess(
                <div className="flex flex-col">
                    <h2 className="font-medium text-gray-900">
                        Data hygiene successfully activated
                    </h2>
                    <p className="text-gray-700">We're in the process of scanning your data...</p>
                </div>,
                { size: 'md' },
            );

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toastError(error?.response?.data?.message ?? `Failed to activate. Please try again.`);
        },
    });

    return (
        <Box className="p-8">
            <div className="@container flex w-full gap-8">
                <div className="@xl:flex-1 flex w-full flex-col gap-8">
                    <div className="flex flex-col gap-4 text-gray-700">
                        <h2>
                            <img
                                src={GivebutterPlusLogo}
                                className="h-auto w-36"
                                alt="Givebutter Plus logo"
                                width={144}
                                height={16}
                                draggable={false}
                            />
                        </h2>
                        <div className="flex flex-col gap-3">
                            <h3 className="text-3xl font-medium text-gray-900">
                                Ready to clean your data?
                            </h3>
                            {isCurrentlyOnFreeTrial ? (
                                <p>
                                    During your free trial, we can clean up to 100 contact emails,
                                    phone numbers, and addresses. Import your contacts and activate
                                    data hygiene to start cleaning up your contact data!
                                </p>
                            ) : (
                                <p>
                                    Activate data hygiene to keep your contact records clean. Once
                                    activated, contact emails, phone numbers, and addresses will be
                                    queued for verification. Once completed, you will be able to
                                    review any corrections before applying them to your contacts.
                                </p>
                            )}
                        </div>
                    </div>
                    <div>
                        {contactsTotal > 0 ? (
                            <Button
                                variant="solid"
                                onClick={() => {
                                    activate();
                                }}
                            >
                                {activateIsPending ? (
                                    <>
                                        <FontAwesomeIcon
                                            className={'animate-spin'}
                                            icon={faSpinnerThird}
                                        />
                                        Activating
                                    </>
                                ) : (
                                    'Activate'
                                )}
                            </Button>
                        ) : (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <Button variant="solid" disabled>
                                        Activate
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse className="!max-w-40">
                                        <p>Import or manually add contacts before activating.</p>
                                        <Tooltip.Arrow inverse />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        )}
                    </div>
                </div>
                <div className="@xl:block relative hidden flex-1">
                    <img
                        src={DataHygieneFeature}
                        alt="Data hygiene illustration"
                        draggable={false}
                        className="ml-auto h-auto w-full max-w-[27rem]"
                    />
                </div>
            </div>
        </Box>
    );
};

export { ActivationInterstitial };
